import { Box, Skeleton, Typography } from "@mui/material";
import dico from "src/shared/util/src/language/dico";

export type PageSkeletonVariant =
  | "grid"
  | "cards"
  | "form"
  | "editor"
  | "dashboard";

/** Stable page geometry while code, permissions or initial data are loading. */
export default function PageSkeleton({
  variant = "grid",
  title,
  contentOnly = false,
}: {
  variant?: PageSkeletonVariant;
  title?: string;
  contentOnly?: boolean;
}) {
  const panel = {
    border: 1,
    borderColor: "divider",
    borderRadius: 1,
    p: 2,
    minWidth: 0,
  };
  return (
    <Box
      role="status"
      aria-label={dico().loading}
      aria-busy="true"
      sx={{
        width: "100%",
        minWidth: 0,
        minHeight: 0,
        flex: 1,
        "@media (prefers-reduced-motion: reduce)": {
          "& .MuiSkeleton-root": { animation: "none" },
        },
      }}
    >
      {title && !contentOnly && (
        <Typography
          component="h1"
          variant="h5"
          sx={{ fontSize: 20, fontWeight: 600, mb: 2 }}
        >
          {title}
        </Typography>
      )}
      <Box
        aria-hidden="true"
        sx={{ display: "flex", flexDirection: "column", gap: 2 }}
      >
        {!contentOnly && !title && (
          <Skeleton variant="rounded" width="35%" height={24} />
        )}
        {variant === "grid" ? (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                py: 1.5,
              }}
            >
              <Skeleton variant="text" width="25%" height={28} />
              <Skeleton
                variant="rounded"
                width={220}
                height={33}
                sx={{ maxWidth: "50%" }}
              />
            </Box>
            <Box sx={{ ...panel, p: 0, overflow: "hidden" }}>
              <Box
                sx={{
                  display: "flex",
                  gap: 3,
                  bgcolor: "action.hover",
                  px: 2,
                  py: 1,
                }}
              >
                {[2, 1, 1, 1].map((width, index) => (
                  <Skeleton key={index} width={`${width * 18}%`} height={18} />
                ))}
              </Box>
              {Array.from({ length: 7 }, (_, row) => (
                <Box
                  key={row}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 3,
                    height: 48,
                    px: 2,
                    borderTop: 1,
                    borderColor: "divider",
                  }}
                >
                  {[34, 18, 18, 12].map((width, cell) => (
                    <Skeleton
                      key={cell}
                      width={`${width}%`}
                      height={16}
                      sx={{ opacity: 1 - row * 0.08 }}
                    />
                  ))}
                </Box>
              ))}
            </Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
                py: 1,
              }}
            >
              <Skeleton width={110} height={20} />
              <Skeleton width={90} height={20} />
              <Skeleton variant="rounded" width={64} height={28} />
            </Box>
          </>
        ) : variant === "editor" ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "190px minmax(0, 1fr) 220px",
              },
              gap: 2,
              height: "min(65dvh, 600px)",
            }}
          >
            {[0, 1, 2].map((column) => (
              <Box
                key={column}
                sx={{
                  ...panel,
                  display: column === 1 ? "block" : { xs: "none", md: "block" },
                }}
              >
                <Skeleton
                  variant="rounded"
                  width="100%"
                  height={column === 1 ? "100%" : 28}
                />
                {column !== 1 &&
                  Array.from({ length: 6 }, (_, row) => (
                    <Skeleton key={row} height={32} sx={{ mt: 2 }} />
                  ))}
              </Box>
            ))}
          </Box>
        ) : variant === "form" ? (
          <Box sx={{ ...panel, maxWidth: 720 }}>
            {Array.from({ length: 5 }, (_, row) => (
              <Box key={row} sx={{ mb: 2 }}>
                <Skeleton width="25%" height={16} sx={{ mb: 0.75 }} />
                <Skeleton variant="rounded" height={34} />
              </Box>
            ))}
            <Skeleton variant="rounded" width={120} height={32} />
          </Box>
        ) : (
          <>
            {variant === "dashboard" && (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
                  gap: 2,
                }}
              >
                {Array.from({ length: 4 }, (_, index) => (
                  <Skeleton key={index} variant="rounded" height={100} />
                ))}
              </Box>
            )}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  lg: "repeat(3, 1fr)",
                },
                gap: 2,
              }}
            >
              {Array.from(
                { length: variant === "dashboard" ? 3 : 6 },
                (_, index) => (
                  <Box key={index} sx={panel}>
                    <Skeleton width="65%" height={26} />
                    <Skeleton width="45%" height={18} sx={{ mt: 1 }} />
                    <Skeleton variant="rounded" height={100} sx={{ mt: 2 }} />
                  </Box>
                ),
              )}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}
